import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { apiCall } from '../api';
import { useMountEffect } from '../hooks/useMountEffect';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalPdfs: 0,
    totalExtractions: 0,
    successfulExtractions: 0,
    pendingExtractions: 0,
    totalBatches: 0,
    successRate: 0,
    completionRate: 0,
    avgProcessingTime: 0
  });
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [recentBatches, setRecentBatches] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [xorbitOrders, setXorbitOrders] = useState([]);
  const [cocbOrders, setCocbOrders] = useState([]);
  const [gfsOrders, setGfsOrders] = useState([]);

  // Check if user is superadmin/admin
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.pipeline === 'all' || user.role === 'superadmin' || user.role === 'admin';

  useMountEffect(() => {
    loadDashboardData();
  });

  const formatProcessingTime = (seconds) => {
    if (!seconds || seconds <= 0) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return `${hrs}h ${remainMins}m`;
  };

  const getProcessingTime = (wo) => {
    const created = wo.createdAt || wo.created_at;
    const updated = wo.updatedAt || wo.updated_at;
    if (!created || !updated) return null;
    if (wo.status !== 'completed') return null;
    const diff = (new Date(updated) - new Date(created)) / 1000;
    return diff > 0 ? diff : null;
  };

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch combined stats from all pipelines
      try {
        const combinedStats = await apiCall('/api/dashboard/combined-stats');
        if (combinedStats && !combinedStats.error) {
          setStats(prev => ({
            ...prev,
            totalPdfs: combinedStats.totalPdfs || 0,
            totalExtractions: combinedStats.totalExtractions || 0,
            successfulExtractions: combinedStats.successfulExtractions || 0,
            pendingExtractions: combinedStats.pendingExtractions || 0,
            successRate: combinedStats.successRate || 0,
            completionRate: combinedStats.completionRate || 0,
            avgProcessingTime: combinedStats.avgProcessingTime || 0
          }));
        }
      } catch (err) {
        console.warn('Failed to load combined stats, falling back to local calc:', err.message);
        // Fallback: calculate from work orders if combined endpoint fails
      }

      let workOrders = [];

      // Fetch latest work orders
      try {
        const woResponse = await apiCall('/api/work-orders?skip=0&limit=10');
        if (woResponse && woResponse.data) {
          workOrders = woResponse.data || [];
        } else if (Array.isArray(woResponse)) {
          workOrders = woResponse;
        }
      } catch (err) {
        console.warn('Failed to load work orders:', err.message);
      }

      setRecentWorkOrders(workOrders);

      // Fetch batch count
      try {
        const batchResp = await apiCall('/api/batch?limit=5');
        if (batchResp && batchResp.pagination) {
          setStats(prev => ({ ...prev, totalBatches: batchResp.pagination.total }));
          setRecentBatches(batchResp.data || []);
        }
      } catch (err) { console.warn('Failed to load batch count:', err.message); }

      // Fetch form types statistics
      try {
        const formTypesResponse = await apiCall('/api/work-orders/form-types/statistics');
        const types = formTypesResponse || [];
        setFormTypes(types);
      } catch (err) {
        console.warn('Failed to load form types:', err.message);
      }

      // Fetch pipeline work orders for superadmin
      if (isSuperAdmin) {
        try {
          const xorbitResp = await apiCall('/v1/work-orders?skip=0&limit=5');
          setXorbitOrders(xorbitResp?.data || []);
        } catch (err) { console.warn('Failed to load XOrbit orders:', err.message); }

        try {
          const cocbResp = await apiCall('/v1/cocb/api/work-orders?skip=0&limit=5');
          setCocbOrders(cocbResp?.data || cocbResp?.work_orders || []);
        } catch (err) { console.warn('Failed to load COCB orders:', err.message); }

        try {
          const gfsResp = await apiCall('/v1/gfs/api/work-orders?skip=0&limit=5');
          setGfsOrders(gfsResp?.data || gfsResp?.work_orders || []);
        } catch (err) { console.warn('Failed to load GFS orders:', err.message); }
      }
    } catch (err) {
      console.error('Unexpected error in loadDashboardData:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Full-page loader
  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loader">
          <div className="loader-spinner"></div>
          <p className="loader-text">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard">
        <div className="dashboard-error">
          <div className="error-icon">⚠️</div>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button onClick={loadDashboardData} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Analytics - Horizontal at top */}
      <div className="analytics-horizontal">
        {/* Left group */}
        <div className="analytics-group left-group">
          <div className="analytics-item success-rate">
            <div className="analytics-icon">✓</div>
            <div className="analytics-label">Success Rate</div>
            <div className="analytics-value">
              {stats.successRate}%
            </div>
          </div>
          <div className="analytics-item processing-time">
            <div className="analytics-icon">⏱</div>
            <div className="analytics-label">Avg. Processing Time</div>
            <div className="analytics-value">{formatProcessingTime(stats.avgProcessingTime)}</div>
          </div>
          <div className="analytics-item completion-rate">
            <div className="analytics-icon">📊</div>
            <div className="analytics-label">Completion Rate</div>
            <div className="analytics-value">
              {stats.completionRate}%
            </div>
          </div>
        </div>

        {/* Right group */}
        <div className="analytics-group right-group">
          <div className="stat-item uploads-stat">
            <div className="stat-icon-mini">📄</div>
            <div className="stat-value-small">{stats.totalPdfs}</div>
            <div className="stat-label-small">PDFs Uploaded</div>
          </div>
          <div className="stat-item extractions-stat">
            <div className="stat-icon-mini">📋</div>
            <div className="stat-value-small">{stats.totalExtractions}</div>
            <div className="stat-label-small">Extractions</div>
          </div>
          <div className="stat-item verified-stat">
            <div className="stat-icon-mini">✅</div>
            <div className="stat-value-small">{stats.successfulExtractions}</div>
            <div className="stat-label-small">Verified</div>
          </div>
          <div className="stat-item pending-stat">
            <div className="stat-icon-mini">⏳</div>
            <div className="stat-value-small">{stats.pendingExtractions}</div>
            <div className="stat-label-small">Pending Review</div>
          </div>
          <div className="stat-item" style={{ cursor: 'pointer' }} onClick={() => navigate('/batch-extract')}>
            <div className="stat-icon-mini">📦</div>
            <div className="stat-value-small">{stats.totalBatches}</div>
            <div className="stat-label-small">Batch Orders</div>
          </div>
        </div>
      </div>

      {/* Pipeline Work Orders (Superadmin only) */}
      {isSuperAdmin && (
        <>
          {/* XOrbit Work Orders */}
          <div className="dashboard-section">
            <h2 style={{ cursor: 'pointer' }} onClick={() => navigate('/v1/dashboard')}>
              <span style={{color: '#8b5cf6'}}>⚡</span> XOrbit / VJA Work Orders <span style={{ fontSize: 13, color: '#8b5cf6', fontWeight: 400 }}>→ View All</span>
            </h2>
            {xorbitOrders.length === 0 ? (
              <div className="empty-state" style={{ padding: 20, color: '#a0aec0' }}>No XOrbit work orders yet</div>
            ) : (
              <div className="form-types-table">
                <table>
                  <thead><tr><th>ID</th><th>Model</th><th>Filename</th><th>Status</th><th>Processing Time</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {xorbitOrders.map(wo => (
                      <tr key={wo._id}>
                        <td><code style={{fontSize:11}}>{(wo._id || '').substring(0,10)}...</code></td>
                        <td><span style={{background:'#f3e8ff',color:'#7c3aed',padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600}}>{wo.modelType || wo.model_type || 'vja'}</span></td>
                        <td style={{fontSize:12}}>{(wo.filename || wo.name || '-').substring(0,30)}</td>
                        <td><span className={`status-badge ${wo.status}`}>{wo.status}</span></td>
                        <td style={{fontSize:12, color: '#6b7280'}}>{formatProcessingTime(getProcessingTime(wo))}</td>
                        <td style={{fontSize:11,color:'#718096'}}>{wo.createdAt || wo.created_at ? new Date(wo.createdAt || wo.created_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata'}) : '-'}</td>
                        <td><button onClick={() => navigate(`/v1/work-order/${wo._id}`)} style={{background:'#8b5cf6',color:'white',border:'none',padding:'4px 10px',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:500}}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* COCB Work Orders */}
          <div className="dashboard-section">
            <h2 style={{ cursor: "pointer" }} onClick={() => navigate("/v1/cocb/dashboard")}>
              <span style={{color: "#f59e0b"}}>📋</span> COCB Work Orders <span style={{ fontSize: 13, color: "#f59e0b", fontWeight: 400 }}>→ View All</span>
            </h2>
            {cocbOrders.length === 0 ? (
              <div className="empty-state" style={{ padding: 20, color: "#a0aec0" }}>No COCB work orders yet</div>
            ) : (
              <div className="form-types-table">
                <table>
                  <thead><tr><th>ID</th><th>Filename</th><th>Status</th><th>Pages</th><th>Processing Time</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {cocbOrders.map(wo => (
                      <tr key={wo._id}>
                        <td><code style={{fontSize:11}}>{(wo._id || "").substring(0,10)}...</code></td>
                        <td style={{fontSize:12}}>{(wo.filename || "-").substring(0,30)}</td>
                        <td><span className={`status-badge ${wo.status}`}>{wo.status}</span></td>
                        <td style={{textAlign:"center"}}>{wo.totalPages || wo.total_pages || "-"}</td>
                        <td style={{fontSize:12, color: '#6b7280'}}>{formatProcessingTime(getProcessingTime(wo))}</td>
                        <td style={{fontSize:11,color:"#718096"}}>{wo.createdAt || wo.created_at ? new Date(wo.createdAt || wo.created_at).toLocaleString("en-IN",{timeZone:"Asia/Kolkata"}) : "-"}</td>
                        <td><button onClick={() => navigate(`/v1/cocb/work-order/${wo._id}`)} style={{background:"#f59e0b",color:"white",border:"none",padding:"4px 10px",borderRadius:4,cursor:"pointer",fontSize:11,fontWeight:500}}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {/* GFS Work Orders */}
          <div className="dashboard-section">
            <h2 style={{ cursor: 'pointer' }} onClick={() => navigate('/gfs/dashboard')}>
              <span style={{color: '#10b981'}}>📄</span> GFS Work Orders <span style={{ fontSize: 13, color: '#10b981', fontWeight: 400 }}>→ View All</span>
            </h2>
            {gfsOrders.length === 0 ? (
              <div className="empty-state" style={{ padding: 20, color: '#a0aec0' }}>No GFS work orders yet</div>
            ) : (
              <div className="form-types-table">
                <table>
                  <thead><tr><th>ID</th><th>Form Type</th><th>Filename</th><th>Status</th><th>Processing Time</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {gfsOrders.map(wo => (
                      <tr key={wo._id}>
                        <td><code style={{fontSize:11}}>{(wo._id || '').substring(0,10)}...</code></td>
                        <td><span style={{background:'#d1fae5',color:'#065f46',padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600}}>{wo.form_type || wo.formType || '-'}</span></td>
                        <td style={{fontSize:12}}>{(wo.filename || '-').substring(0,30)}</td>
                        <td><span className={`status-badge ${wo.status}`}>{wo.status}</span></td>
                        <td style={{fontSize:12, color: '#6b7280'}}>{formatProcessingTime(getProcessingTime(wo))}</td>
                        <td style={{fontSize:11,color:'#718096'}}>{wo.createdAt || wo.created_at ? new Date(wo.createdAt || wo.created_at).toLocaleString('en-IN',{timeZone:'Asia/Kolkata'}) : '-'}</td>
                        <td><button onClick={() => navigate(`/gfs/work-order/${wo._id}`)} style={{background:'#10b981',color:'white',border:'none',padding:'4px 10px',borderRadius:4,cursor:'pointer',fontSize:11,fontWeight:500}}>View</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Recent Batch Orders */}
      <div className="dashboard-section">
        <h2 style={{ cursor: 'pointer' }} onClick={() => navigate('/batch-extract')}>Recent Batch Orders <span style={{ fontSize: 13, color: '#3182ce', fontWeight: 400 }}>→ View All</span></h2>
        {recentBatches.length === 0 ? (
          <div className="empty-state" style={{ padding: 20, color: '#a0aec0' }}>No batch orders yet</div>
        ) : (
          <div className="form-types-table">
            <table>
              <thead>
                <tr>
                  <th>Batch ID</th>
                  <th>Report Type</th>
                  <th style={{ textAlign: 'center' }}>Work Orders</th>
                  <th style={{ textAlign: 'center' }}>Completed</th>
                  <th style={{ textAlign: 'center' }}>Failed</th>
                  <th>Created At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentBatches.map((b, idx) => (
                  <tr key={idx}>
                    <td><code style={{ fontSize: 11 }}>{b.batchId}</code></td>
                    <td>{b.reportType || '-'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{b.total}</td>
                    <td style={{ textAlign: 'center', color: '#10b981' }}>{b.completed}</td>
                    <td style={{ textAlign: 'center', color: '#e53e3e' }}>{b.failed}</td>
                    <td style={{ fontSize: 12, color: '#718096' }}>{b.createdAt ? new Date(b.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                    <td>
                      <button onClick={() => navigate(`/batch/${b.batchId}`)} style={{ background: '#EBF8FF', border: 'none', color: '#3182ce', cursor: 'pointer', fontSize: 12, padding: '4px 10px', borderRadius: 4, fontWeight: 500 }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Work Orders */}
      <div className="dashboard-section">
        <h2 style={{ cursor: 'pointer' }} onClick={() => navigate('/work-orders')}>Recent Work Orders <span style={{ fontSize: 13, color: '#3182ce', fontWeight: 400 }}>→ View All</span></h2>
        {recentWorkOrders.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <p>No work orders yet</p>
            <small>Create a work order to begin processing</small>
          </div>
        ) : (
          <div className="files-table">
            <table>
              <thead>
                <tr>
                  <th>Work Order ID</th>
                  <th>Form Type</th>
                  <th>Customer</th>
                  <th>Created Date</th>
                  <th>Created By</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentWorkOrders.map(wo => (
                  <tr key={wo._id}>
                    <td className="filename" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {wo._id.substring(0, 12)}...
                    </td>
                    <td>{wo.reportType || 'Unknown'}</td>
                    <td>{wo.customerName || '-'}</td>
                    <td>{wo.createdAt ? new Date(wo.createdAt).toLocaleString() : 'N/A'}</td>
                    <td style={{ fontSize: '0.9rem', color: '#6b7280' }}>{wo.createdBy || '-'}</td>
                    <td>
                      <span className={`status-badge ${wo.status || 'pending'}`}>
                        {wo.status === 'completed' ? 'Processed' : 
                         wo.status === 'processing' ? 'Processing' : 
                         wo.status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/work-order/${wo._id}`)}
                        style={{
                          background: wo.status === 'completed' ? '#667eea' : '#94a3b8',
                          color: 'white',
                          border: 'none',
                          padding: '0.4rem 0.8rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.opacity = '0.9'}
                        onMouseOut={(e) => e.target.style.opacity = '1'}
                      >
                        {wo.status === 'completed' ? 'Review' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

export default Dashboard;
